import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {ApiModelService} from "../sp-api-model/api-model.service";
import {Audience} from "../../../models/audience";
import {Targeting} from "../../../classes/targeting/targeting";
import { toFbJsonString } from "../../../classes/targeting/meta-targeting";
import {Project} from '../../../models/project';
import {LoggerService} from '../../sp-logger/logger.service';
import {firstValueFrom} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ApiAudienceService extends ApiModelService<Audience> {
  protected readonly basePath = "audiences";

  constructor(httpClient: HttpClient, logger: LoggerService) {
    super(httpClient, logger);
  }

  audienceSize(targeting: Targeting, type: string, extra: string): Promise<{ size: number, score: number, score_v2: string }> {
    return firstValueFrom(this.post<{size: number, score: number, score_v2: string}>(`${this.basePath}/size`, {target: toFbJsonString(targeting), type, extra}));
  }

  /* Other methods with relations go here */

  audienceLatestCrawlHasPersona(audienceId: number, universeId?: number): Promise<{valid: boolean}> {
    return firstValueFrom(this.get<{valid: boolean}>(`${this.basePath}/${audienceId}/crawls/latest/validations/has-persona`, { universeId }));
  }

  personaTargeting(project: Project, personaId: number) {
    // Abs value for personaId
    personaId = Math.abs(personaId);
    return new Promise<{target: any, benchmark: any}>((resolve, reject) => {
      const sub = this.get<{target: any, benchmark: any}>(`${this.basePath}/${project.audience_target.id}/${project.audience_bench.id}/persona/${personaId}/targeting`).subscribe(result => {
        resolve(result);
      }, error => {
        reject(error);
      }, () => sub.unsubscribe());
    })
  }

  /**
   * Creates multiple standalone projects from personas with crossed targeting.
   * Story 5: Multiple Persona Project Creation feature
   * Backend handles transaction safety, credit validation, and batch processing.
   * @param projectId The source project ID
   * @param personas Array of {personaId, title} for each project to create
   * @returns Promise with created projects and credits used
   */
  personaAsProjectBatch(
    projectId: number,
    personas: Array<{personaId: number, title: string}>
  ): Promise<{projects: Project[], creditsUsed: number}> {
    // Ensure all personaIds are positive
    const sanitizedPersonas = personas.map(p => ({
      personaId: Math.abs(p.personaId),
      title: p.title
    }));

    return firstValueFrom(this.post<{projects: Project[], creditsUsed: number}>(
      `projects/${projectId}/personas/as-projects`,
      { personas: sanitizedPersonas }
    ));
  }

  /* ------------------------------------ */
}
